import { getServerInfo, type ServerInfo } from "@/api/serverInfo";
import type { Lifecycle } from "@/lifecycle";
import { createLogger } from "@/logging";

export type ServerInfoSnapshot = {
  data: ServerInfo | null;
  // 다시 조회하는 중에도 true 다. 마스크는 현재 설정 확인이 끝난 뒤에만 쓸 수 있다.
  loading: boolean;
  failed: boolean;
};

const logger = createLogger("app/serverInfo");
const pollIntervalMs = 5 * 60_000;
const emptySnapshot = (): ServerInfoSnapshot => ({
  data: null,
  loading: true,
  failed: false,
});

/** 로그인 세션 동안 배포 정보와 기능 설정을 한 번씩 조회해 모든 화면에 공유한다. */
export class ServerInfoController implements Lifecycle {
  private snapshot = emptySnapshot();
  private readonly listeners = new Set<() => void>();
  private active = false;
  private timer: ReturnType<typeof setInterval> | null = null;
  private request: AbortController | null = null;

  getSnapshot = (): ServerInfoSnapshot => this.snapshot;
  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  init(): void {
    if (this.active) return;
    this.active = true;
    document.addEventListener("visibilitychange", this.onVisibilityChange);
    this.onVisibilityChange();
  }

  dispose(): void {
    this.active = false;
    document.removeEventListener("visibilitychange", this.onVisibilityChange);
    this.stopTimer();
    this.request?.abort();
    this.request = null;
    this.publish(emptySnapshot());
  }

  refresh = async (): Promise<void> => {
    if (!this.active || this.request !== null) return;
    const request = new AbortController();
    this.request = request;
    this.publish({ ...this.snapshot, loading: true });
    try {
      const data = await getServerInfo({ signal: request.signal });
      if (this.request === request) this.publish({ data, loading: false, failed: false });
    } catch (error) {
      if (this.request !== request) return;
      logger.error("Failed to load server info.", error);
      this.publish({ ...this.snapshot, loading: false, failed: true });
    } finally {
      if (this.request === request) this.request = null;
    }
  };

  /** 서버가 거부한 설정을 즉시 폐기한다. 먼저 시작한 조회의 결과도 이 거부를 덮어쓰지 못한다. */
  invalidateCapabilities(): void {
    this.request?.abort();
    this.request = null;
    this.publish({ ...this.snapshot, failed: true });
    void this.refresh();
  }

  private readonly onVisibilityChange = (): void => {
    this.stopTimer();
    if (document.visibilityState === "hidden") return;
    void this.refresh();
    this.timer = setInterval(() => void this.refresh(), pollIntervalMs);
  };

  private stopTimer(): void {
    if (this.timer !== null) clearInterval(this.timer);
    this.timer = null;
  }

  private publish(snapshot: ServerInfoSnapshot): void {
    this.snapshot = snapshot;
    this.listeners.forEach((listener) => listener());
  }
}

export const serverInfoController = new ServerInfoController();
